import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconSchool, IconBolt, IconClockHour4, IconCertificate2, IconPlayerPlayFilled, IconAdjustmentsHorizontal,
    IconTrophy, IconTarget, IconStarFilled, IconBook2, IconHistory, IconCheck, IconFlame, IconDeviceFloppy, IconSparkles, IconRotate,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ConfirmDialog from '../../../../../components/common/ConfirmDialog';

import Stars from './components/Stars';
import PracticeWindow from './components/PracticeWindow';
import SettingsPanel, { DEFAULT_PREFS } from './components/SettingsPanel';

import {
    createStrictSession, strictPress, expectedStroke,
    createFreeSession, freePress, finishSession, freeWordCorrect,
} from './engine/session';
import { QWERTY, QWERTY_STROKE_OF } from './engine/keyboardGeometry';
import { scoreStrict, scoreFree, starsFor, perKeyStats, wpmTimeline } from './engine/metrics';
import { createKeyModel, updateKeyModel, adaptiveState, adaptiveProgress, msPerStrokeAt } from './engine/adaptive';
import { loadProgress, saveProgress, recordLesson, recordRun, saveModel, savePrefs, resetProgress } from './engine/progress';
import { playKey, playError, playFinish, warmAudio } from './engine/sound';
import {
    getLayout, textForMode, freeHint, freeWordOnTrack, cumulativeKeys, activeCodesFor, readKeyEvent, courseFor, passagesFor,
} from './typingEngine';

// Usage is counted under the tool's slug (no /tools prefix) — the page's "used N times" reads the same key.
const TOOL_SLUG = 'general/typing-practice';
const LAYOUT_IDS = ['bijoy', 'avro', 'english'];
const MODES = ['learn', 'practice', 'test', 'exam'];
const MODE_ICON = { learn: IconSchool, practice: IconBolt, test: IconClockHour4, exam: IconCertificate2 };
// Each mode gets its own soft tint so the four cards read apart at a glance.
const MODE_TINT = {
    learn: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    practice: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
    test: 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400',
    exam: 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400',
};
const DURATIONS = [60, 120, 180, 300];
const EXAM_PRESETS = [
    { id: 'office20', bnTarget: 20, enTarget: 20, minutes: 5 },
    { id: 'office2530', bnTarget: 25, enTarget: 30, minutes: 5 },
    { id: 'steno2530', bnTarget: 25, enTarget: 30, minutes: 10 },
];
const ADAPTIVE_TARGET = 25;

export default function TypingPracticeGeneral() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const n = useCallback((v) => (isBn ? toBnDigits(String(v)) : String(v)), [isBn]);

    const [layoutId, setLayoutId] = useState('bijoy');
    const [mode, setMode] = useState('learn');
    const [progress, setProgress] = useState(() => loadProgress());
    const [lessonIndex, setLessonIndex] = useState(() => firstUndone(courseFor('bijoy'), loadProgress().layouts.bijoy.lessons));
    const [duration, setDuration] = useState(60);
    const [examIndex, setExamIndex] = useState(0);
    const [examPreset, setExamPreset] = useState(0);
    const [examTab, setExamTab] = useState('post'); // exam setup tab: 'post' | 'passage'

    const layoutProgress = progress.layouts[layoutId];
    const prefs = useMemo(() => ({ ...DEFAULT_PREFS, ...(progress.prefs || {}) }), [progress.prefs]);
    const course = useMemo(() => courseFor(layoutId), [layoutId]);
    const passages = useMemo(() => passagesFor(layoutId), [layoutId]);
    const layout = getLayout(layoutId);

    const model = useMemo(() => layoutProgress.model || createKeyModel(layoutId), [layoutProgress.model, layoutId]);
    const adaptive = useMemo(() => adaptiveState(model, ADAPTIVE_TARGET), [model]);

    // ---- run state ----
    const [phase, setPhase] = useState('setup'); // setup | typing | done — the practice window is open unless 'setup'
    const [session, setSession] = useState(null);
    const [sessionKind, setSessionKind] = useState('strict'); // strict | free
    const [runMode, setRunMode] = useState('learn'); // the mode the open run was started in
    const [pressedCode, setPressedCode] = useState(null);
    const [imeWarning, setImeWarning] = useState(false);
    const [remaining, setRemaining] = useState(null); // seconds, test/exam
    const [typingStarted, setTypingStarted] = useState(false); // first keystroke of a run — starts the clock
    const [wordSelected, setWordSelected] = useState(false); // Ctrl+A / double-click highlighted the current word
    const [result, setResult] = useState(null);
    const [shakeAt, setShakeAt] = useState(0);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [now, setNow] = useState(() => performance.now()); // same clock as the session's timestamps
    const sessionRef = useRef(null);
    sessionRef.current = session;
    const endTimeRef = useRef(null);
    const pressTimer = useRef(null);
    const dashboardRef = useRef(null);

    const isBanglaLayout = layout.script === 'bengali'; // Bijoy and Avro share the Bangla targets
    const examTarget = EXAM_PRESETS[examPreset][isBanglaLayout ? 'bnTarget' : 'enTarget'];
    const activeCodes = useMemo(() => {
        if (runMode !== 'learn') return null;
        return activeCodesFor(cumulativeKeys(course, lessonIndex, layoutId), { caps: course[lessonIndex]?.caps });
    }, [runMode, course, lessonIndex, layoutId]);

    const persist = useCallback((updater) => {
        setProgress((prev) => {
            const next = updater(prev);
            saveProgress(next);
            return next;
        });
    }, []);
    const changePrefs = useCallback((patch) => persist((prev) => savePrefs(prev, patch)), [persist]);

    const switchLayout = (id) => {
        setLayoutId(id);
        setLessonIndex(firstUndone(courseFor(id), progress.layouts[id].lessons));
        setExamIndex(0);
    };

    // ---- start / stop ----
    const start = useCallback((opts = {}) => {
        const li = opts.lessonIndex ?? lessonIndex;
        const text = textForMode({ mode, layoutId, course, lessonIndex: li, duration, passage: passages[examIndex], model, adaptiveState: adaptive, seed: Math.floor(Math.random() * 1e6) });
        if (!text) return;
        // The button that opened the window keeps focus otherwise, and Enter would press it again.
        document.activeElement?.blur?.();
        if (prefs.sound) warmAudio(); // start the audio engine now, so the first key is not late
        if (li !== lessonIndex) setLessonIndex(li);
        setRunMode(mode);
        setImeWarning(false);
        setResult(null);
        setTypingStarted(false);
        setWordSelected(false);
        setSettingsOpen(false);
        endTimeRef.current = null; // set on the first keystroke
        let next;
        if (mode === 'learn' || mode === 'practice') {
            setSessionKind('strict');
            next = createStrictSession(layout, text);
            setRemaining(null);
        } else {
            setSessionKind('free');
            next = createFreeSession(layout, text, { allowBackIntoCorrect: false });
            setRemaining(mode === 'test' ? duration : EXAM_PRESETS[examPreset].minutes * 60);
        }
        sessionRef.current = next;
        setSession(next);
        setPhase('typing');
        // One use per practice started (Start / Retry / Next lesson all come through here),
        // not only on a finished run.
        recordToolUsage(TOOL_SLUG, { category: 'result', metadata: { layout: layoutId, mode } });
    }, [mode, layoutId, course, lessonIndex, duration, passages, examIndex, model, adaptive, layout, examPreset, prefs.sound]);

    const closeWindow = useCallback(() => {
        setPhase('setup');
        setSession(null);
        setResult(null);
        setRemaining(null);
        setSettingsOpen(false);
        endTimeRef.current = null;
    }, []);

    const finishRun = useCallback((finalSession) => {
        const s = finalSession || sessionRef.current;
        if (!s) return;
        setPhase('done');
        setSettingsOpen(false);
        let success;
        if (sessionKind === 'strict') {
            const scored = scoreStrict(s);
            const lesson = course[lessonIndex];
            const goal = isBanglaLayout ? 20 : 25;
            const star = runMode === 'learn' ? starsFor(scored, { minWpm: 8, goalWpm: goal }) : { stars: null, passed: scored.accuracy >= 90 };
            const keyStats = perKeyStats(s, layout);
            const weakKeys = Object.entries(keyStats)
                .filter(([, v]) => v.errors > 0)
                .sort((a, b) => b[1].errors - a[1].errors)
                .slice(0, 8)
                .map(([key, v]) => ({ key, label: key, errors: v.errors }));
            const chart = wpmTimeline(s.log, { isStroke: (e) => e.ok });
            setResult({ kind: 'strict', data: scored, stars: star.stars, passed: star.passed, chart, weakKeys });
            success = star.passed;
            // The adaptive model is keyed by physical key characters (its unlock order is
            // 'fcvg…'), so feed it per-key timings under those, not the printed glyphs.
            const modelStats = perKeyStats(s, { statKey: modelKeyOf(layoutId) });
            const samples = {};
            Object.entries(modelStats).forEach(([k, v]) => { samples[k] = { meanMs: v.meanMs, total: v.total }; });
            persist((prev) => {
                let next = updateKeyModelInProgress(prev, layoutId, samples);
                if (runMode === 'learn') next = recordLesson(next, layoutId, lesson.id, { stars: star.stars, wpm: scored.wpm, accuracy: scored.accuracy });
                next = recordRun(next, layoutId, { at: Date.now(), mode: runMode, wpm: scored.wpm, accuracy: scored.accuracy });
                return next;
            });
        } else {
            const target = runMode === 'exam' ? examTarget : null;
            const scored = scoreFree(s, { target });
            const chart = wpmTimeline(s.log, { isStroke: (e) => e.kind === 'key' });
            setResult({ kind: 'free', data: scored, stars: null, passed: runMode === 'exam' ? scored.passed : null, chart, target });
            success = runMode === 'exam' ? scored.passed : true;
            persist((prev) => recordRun(prev, layoutId, { at: Date.now(), mode: runMode, wpm: scored.netWpm, accuracy: scored.accuracy }));
        }
        if (prefs.sound && prefs.finishSound) playFinish(prefs.soundPack, { volume: prefs.volume, success });
        // Usage is counted when a practice is STARTED (see start()), not again on finish.
    }, [sessionKind, course, lessonIndex, layoutId, runMode, layout, persist, examTarget, prefs]);

    // ---- countdown for test / exam ----
    // Runs only once typing has begun: the first keystroke sets endTimeRef and flips
    // typingStarted, which re-fires this effect so the countdown ticks from that moment.
    useEffect(() => {
        if (phase !== 'typing' || sessionKind !== 'free' || remaining == null) return undefined;
        if (!typingStarted || !endTimeRef.current) return undefined;
        const id = setInterval(() => {
            const left = Math.ceil((endTimeRef.current - Date.now()) / 1000);
            setRemaining(Math.max(0, left));
            if (left <= 0) {
                clearInterval(id);
                finishRun(finishSession(sessionRef.current));
            }
        }, 200);
        return () => clearInterval(id);
    }, [phase, sessionKind, typingStarted, remaining != null, finishRun]); // eslint-disable-line react-hooks/exhaustive-deps

    // A clock for the live speed and the elapsed time of untimed drills.
    useEffect(() => {
        if (phase !== 'typing' || !typingStarted) return undefined;
        const id = setInterval(() => setNow(performance.now()), 250);
        return () => clearInterval(id);
    }, [phase, typingStarted]);

    // ---- typing ----
    const sound = useCallback((kind, code) => {
        if (!prefs.sound) return;
        if (kind === 'error') {
            if (prefs.errorSound) playError(prefs.volume);
            else playKey(prefs.soundPack, { kind: 'key', code, volume: prefs.volume });
            return;
        }
        playKey(prefs.soundPack, { kind, code, volume: prefs.volume });
    }, [prefs]);

    const onTypingKey = useCallback((e) => {
        // Free modes: the input is our own widget, so the browser's select-all can't
        // touch it. Ctrl/Cmd+A selects the current word here, and the next Backspace,
        // Delete or typed key clears the whole word at once.
        if (sessionKind === 'free') {
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyA') { e.preventDefault(); if (sessionRef.current?.rendered[sessionRef.current.index]) setWordSelected(true); return; }
            if (wordSelected && (e.code === 'Backspace' || e.code === 'Delete')) {
                e.preventDefault();
                const next = freePress(sessionRef.current, layout, { code: 'Backspace', shift: false, wholeWord: true });
                sessionRef.current = next; setSession(next); setWordSelected(false);
                sound('back', 'Backspace');
                return;
            }
        }

        const sig = readKeyEvent(e);
        if (!sig) return;
        if (sig.type === 'ime') { setImeWarning(true); e.preventDefault(); return; }
        e.preventDefault();
        if (e.repeat && sig.type === 'stroke') return; // a held key is not typing
        setImeWarning(false);
        const s = sessionRef.current;
        if (!s || s.finishedAt) return;

        if (sessionKind === 'strict') {
            if (sig.type !== 'stroke') return; // strict ignores space/backspace
            const next = strictPress(s, sig.stroke);
            sessionRef.current = next; // keep the ref ahead of the next keydown — fast typists outrun React's commit
            setSession(next);
            if (!typingStarted) setTypingStarted(true);
            if (next.flash !== s.flash) { setShakeAt(Date.now()); sound('error', sig.stroke.code); } else sound(sig.stroke.code === 'Space' ? 'space' : 'key', sig.stroke.code);
            if (next.finishedAt) finishRun(next);
        } else {
            // a real keystroke while the word is selected replaces it: clear first.
            let working = s;
            if (wordSelected) {
                working = freePress(s, layout, { code: 'Backspace', shift: false, wholeWord: true });
                setWordSelected(false);
            }
            // first keystroke starts the clock
            if (!endTimeRef.current && sig.type === 'stroke') {
                endTimeRef.current = Date.now() + (remaining || 0) * 1000;
                setTypingStarted(true); // kicks the countdown effect to life
            }
            let stroke;
            if (sig.type === 'space') stroke = { code: 'Space', shift: false };
            else if (sig.type === 'backspace') stroke = { code: 'Backspace', shift: false, wholeWord: sig.whole };
            else stroke = sig.stroke;
            const next = freePress(working, layout, stroke);
            sessionRef.current = next; // same reason as the strict branch
            setSession(next);
            // Every key that leaves (or keeps) the word off its correct path buzzes,
            // until Backspace brings it back; Space buzzes when it ends a wrong word.
            if (stroke.code === 'Backspace') sound('back', 'Backspace');
            else if (stroke.code === 'Space') sound(freeWordOnTrack(working, layout) && isWordComplete(working) ? 'space' : 'error', 'Space');
            else sound(freeWordOnTrack(next, layout) ? 'key' : 'error', stroke.code);
            if (next.finishedAt) finishRun(next);
        }
        if (sig.stroke) {
            setPressedCode(sig.stroke.code);
            window.clearTimeout(pressTimer.current);
            pressTimer.current = window.setTimeout(() => setPressedCode(null), 90);
        }
    }, [sessionKind, layout, remaining, finishRun, wordSelected, typingStarted, sound]);

    const nextOrRetry = useCallback(() => {
        const canNext = runMode === 'learn' && result?.passed && lessonIndex < course.length - 1;
        if (canNext) start({ lessonIndex: lessonIndex + 1 });
        else start();
    }, [runMode, result, lessonIndex, course.length, start]);

    // While the practice window is open every key goes to it — a window listener, so
    // the run never stalls because some element lost focus.
    const windowKeyRef = useRef(null);
    windowKeyRef.current = (e) => {
        if (settingsOpen) {
            if (e.key === 'Escape') { e.preventDefault(); setSettingsOpen(false); document.activeElement?.blur?.(); }
            return;
        }
        if (e.target?.closest?.('[data-typing-ignore]')) return;
        if (e.key === 'Escape') { e.preventDefault(); closeWindow(); return; }
        if (e.key === 'Tab') { e.preventDefault(); start(); return; }
        if (phase === 'done') {
            if (e.key === 'Enter') { e.preventDefault(); nextOrRetry(); }
            return;
        }
        onTypingKey(e);
    };
    const windowOpen = phase !== 'setup';
    useEffect(() => {
        if (!windowOpen) return undefined;
        const onKey = (e) => windowKeyRef.current(e);
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [windowOpen]);

    // On the page itself, Enter starts the chosen session.
    useEffect(() => {
        if (windowOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Enter' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
            const el = e.target;
            const onPage = el === document.body || dashboardRef.current?.contains(el);
            if (!onPage || el.closest?.('input, textarea, select, [contenteditable="true"]')) return;
            e.preventDefault();
            start();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [windowOpen, start]);

    const nextStroke = useMemo(() => {
        if (phase !== 'typing' || !session) return null;
        return sessionKind === 'strict' ? expectedStroke(session) : freeHint(session, layout);
    }, [phase, session, sessionKind, layout]);

    // live stats during a run
    const liveStats = useMemo(() => {
        if (!session) return null;
        if (sessionKind === 'strict') {
            const correct = session.log.filter((e) => e.ok).length;
            const total = session.log.length;
            const mins = session.startedAt ? Math.max((now - session.startedAt) / 60000, 1 / 60) : 0;
            return { wpm: mins ? (correct / 5) / mins : 0, acc: total ? (correct / total) * 100 : 100, progress: session.index / session.units.length };
        }
        const typedWords = session.index;
        const correct = session.words.filter((_, i) => i < session.index && freeWordCorrect(session, i)).length;
        const mins = session.startedAt ? Math.max((now - session.startedAt) / 60000, 1 / 60) : 0;
        const timeShare = remaining != null && runMode === 'test' ? 1 - remaining / duration : 0;
        return {
            wpm: mins ? (session.keystrokes / 5) / mins : 0,
            acc: typedWords ? (correct / typedWords) * 100 : 100,
            progress: runMode === 'test' ? timeShare : session.index / session.words.length,
        };
    }, [session, sessionKind, now, remaining, runMode, duration]);

    const clockSeconds = sessionKind === 'free'
        ? (remaining ?? 0)
        : session?.startedAt ? Math.max(0, Math.floor(((session.finishedAt || now) - session.startedAt) / 1000)) : 0;
    const clock = n(`${Math.floor(clockSeconds / 60)}:${String(clockSeconds % 60).padStart(2, '0')}`);

    // ---------- derived view data ----------
    const history = layoutProgress.history || [];
    // Reset covers both layouts, so its counts (and whether it is enabled) do too.
    const allLayouts = Object.values(progress.layouts);
    const totalRuns = allLayouts.reduce((sum, l) => sum + (l.history?.length || 0), 0);
    const totalLessons = allLayouts.reduce((sum, l) => sum + Object.keys(l.lessons || {}).length, 0);
    const hasProgress = totalRuns > 0 || totalLessons > 0 || allLayouts.some((l) => l.model);
    const stats = useMemo(() => {
        const recent = history.slice(-10);
        const stars = course.reduce((sum, l) => sum + (layoutProgress.lessons[l.id]?.stars || 0), 0);
        const done = course.filter((l) => layoutProgress.lessons[l.id]).length;
        return {
            best: history.length ? Math.max(...history.map((r) => r.wpm || 0)) : null,
            accuracy: recent.length ? recent.reduce((s, r) => s + (r.accuracy || 0), 0) / recent.length : null,
            stars,
            maxStars: course.length * 5,
            done,
            spark: history.slice(-24).map((r) => r.wpm || 0),
            // Nothing practised yet in this layout: the stat tiles would only show dashes and zeros.
            empty: !history.length && !done,
        };
    }, [history, course, layoutProgress.lessons]);

    const keyLabel = useCallback((ch) => {
        const stroke = QWERTY_STROKE_OF[ch];
        return stroke ? layout.strokeLabel(stroke) || ch : ch;
    }, [layout]);

    const statusText = phase === 'typing'
        ? t('typingPractice.status.typing')
        : phase === 'done' ? t('typingPractice.status.done') : t('typingPractice.status.ready');
    const howToSteps = [t('typingPractice.howTo.1'), t('typingPractice.howTo.2'), t('typingPractice.howTo.3'), t('typingPractice.howTo.4')];

    // What the selected mode will run, for the start bar and the window header.
    const summaryFor = (m) => {
        if (m === 'learn') return `${t('typingPractice.lessonNum', { n: n(lessonIndex + 1), total: n(course.length) })} · ${course[lessonIndex]?.title}`;
        if (m === 'practice') return t('typingPractice.adaptive.unlocked', { n: n(adaptive.included.length) });
        if (m === 'test') return `${n(duration / 60)} ${t('typingPractice.minutes')} · ${t('typingPractice.summary.randomWords')}`;
        return `${passages[examIndex]?.title} · ${n(examTarget)} ${t('typingPractice.wpmShort')}, ${n(EXAM_PRESETS[examPreset].minutes)} ${t('typingPractice.minutes')}`;
    };

    /* ------------------------------------------------------------- page */

    const hero = (
        <section className="relative overflow-hidden rounded-[24px] border border-[#EEE9E4] bg-white p-4 shadow-[0_1px_3px_rgba(28,24,21,0.05)] dark:border-zinc-800 dark:bg-zinc-900 sm:p-5">
            <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/[0.08] blur-2xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-hind text-[17px] font-bold text-[#1C1815] dark:text-zinc-100">{t('typingPractice.overview')}</h2>
                    <p className="text-[12.5px] text-[#8A837C] dark:text-zinc-400">{t('typingPractice.heroHint')}</p>
                </div>
                <div className="inline-flex rounded-2xl bg-[#F4F1ED] p-1 dark:bg-zinc-800">
                    {LAYOUT_IDS.map((id) => (
                        <button key={id} type="button" onClick={() => switchLayout(id)}
                            className={`rounded-xl px-4 py-2 font-hind text-[13.5px] font-bold transition-all ${layoutId === id
                                ? 'bg-white text-primary shadow-[0_1px_3px_rgba(28,24,21,0.14)] dark:bg-zinc-700'
                                : 'text-[#8A837C] hover:text-[#3D3833] dark:text-zinc-400 dark:hover:text-zinc-200'}`}>
                            {t(`typingPractice.layout.${id}`)}
                        </button>
                    ))}
                </div>
            </div>

            {!stats.empty && (
                <div className="relative mt-4 grid grid-cols-2 gap-2.5 md:grid-cols-4">
                    <HeroStat icon={IconTrophy} tint="text-primary bg-primary/10" label={t('typingPractice.stat.best')} value={stats.best != null ? n(Math.round(stats.best)) : '—'} unit={stats.best != null ? t('typingPractice.wpmShort') : ''} />
                    <HeroStat icon={IconTarget} tint="text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400" label={t('typingPractice.stat.accuracy')} value={stats.accuracy != null ? `${n(Math.round(stats.accuracy))}%` : '—'} />
                    <HeroStat icon={IconBook2} tint="text-sky-600 bg-sky-50 dark:bg-sky-900/20 dark:text-sky-400" label={t('typingPractice.stat.lessons')} value={n(stats.done)} unit={`/ ${n(course.length)}`} />
                    <HeroStat icon={IconStarFilled} tint="text-amber-500 bg-amber-50 dark:bg-amber-900/20" label={t('typingPractice.starsEarned')} value={n(stats.stars)} unit={`/ ${n(stats.maxStars)}`} />
                </div>
            )}

            {stats.spark.length > 1 && (
                <div className="relative mt-3 flex items-center gap-3 rounded-2xl bg-[#FAF8F6] px-3.5 py-2.5 dark:bg-zinc-800/50">
                    <IconFlame size={16} className="flex-none text-primary" />
                    <span className="flex-none text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">{t('typingPractice.stat.trend')}</span>
                    <Sparkline values={stats.spark} />
                </div>
            )}
        </section>
    );

    const modeCards = (
        <section>
            <SectionHead>{t('typingPractice.chooseMode')}</SectionHead>
            <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
                {MODES.map((m) => {
                    const Icon = MODE_ICON[m];
                    const active = mode === m;
                    return (
                        <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={active}
                            className={`group relative flex flex-col items-start gap-2.5 rounded-[20px] border bg-white p-3.5 text-left transition-all duration-200 dark:bg-zinc-900 sm:p-4 ${active
                                ? 'border-primary shadow-[0_0_0_3px_rgba(230,138,0,0.12),0_8px_24px_-8px_rgba(230,138,0,0.35)]'
                                : 'border-[#EEE9E4] hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_6px_18px_-8px_rgba(28,24,21,0.18)] dark:border-zinc-800'}`}>
                            <div className="flex w-full items-center justify-between">
                                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? 'bg-primary text-white shadow-[0_4px_12px_rgba(230,138,0,0.35)]' : MODE_TINT[m]}`}>
                                    <Icon size={21} stroke={1.8} />
                                </span>
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${active ? 'border-primary bg-primary text-white' : 'border-[#DDD6CF] dark:border-zinc-700'}`}>
                                    {active && <IconCheck size={12} stroke={3} />}
                                </span>
                            </div>
                            <div>
                                <div className="font-hind text-[15px] font-bold text-[#1C1815] dark:text-zinc-100">{t(`typingPractice.mode.${m}`)}</div>
                                <div className="mt-0.5 font-hind text-[12px] leading-snug text-[#8A837C] dark:text-zinc-400">{t(`typingPractice.modeDesc.${m}`)}</div>
                            </div>
                            <span className="mt-auto rounded-full bg-[#F4F1ED] px-2 py-0.5 text-[10.5px] font-bold text-[#7A726B] dark:bg-zinc-800 dark:text-zinc-400">
                                {m === 'learn' ? t('typingPractice.meta.lessons', { n: n(course.length) })
                                    : m === 'practice' ? t('typingPractice.meta.adaptive')
                                        : m === 'test' ? t('typingPractice.meta.minutes', { a: n(1), b: n(5) })
                                            : t('typingPractice.meta.exam')}
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );

    const config = (
        <section className="overflow-hidden rounded-[24px] border border-[#EEE9E4] bg-white shadow-[0_1px_3px_rgba(28,24,21,0.05)] dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-start gap-2.5 border-b border-[#F1EDE9] bg-[#FCFBFA] px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/60 sm:px-5">
                <IconSparkles size={16} className="mt-0.5 flex-none text-primary" />
                <p className="font-hind text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{t(`typingPractice.modeHint.${mode}`)}</p>
            </div>

            <div className="p-4 sm:p-5">
                {mode === 'learn' && (
                    <LessonMap course={course} lessons={layoutProgress.lessons} value={lessonIndex} onChange={setLessonIndex} n={n} t={t} keyLabel={keyLabel} />
                )}
                {mode === 'practice' && (
                    <AdaptivePanel adaptive={adaptive} model={model} progress={adaptiveProgress(model, ADAPTIVE_TARGET)} keyLabel={keyLabel} n={n} t={t} />
                )}
                {mode === 'test' && (
                    <div>
                        <FieldLabel>{t('typingPractice.durationLabel')}</FieldLabel>
                        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                            {DURATIONS.map((d) => (
                                <button key={d} type="button" onClick={() => setDuration(d)} aria-pressed={duration === d}
                                    className={`relative flex flex-col items-center rounded-2xl border py-4 transition-all ${duration === d
                                        ? 'border-primary bg-primary/[0.06] shadow-[0_0_0_3px_rgba(230,138,0,0.10)]'
                                        : 'border-[#EEE9E4] hover:border-primary/40 dark:border-zinc-800'}`}>
                                    <CheckDot on={duration === d} />
                                    <span className={`text-[28px] font-bold leading-none tabular-nums ${duration === d ? 'text-primary' : 'text-[#2C2722] dark:text-zinc-200'}`}>{n(d / 60)}</span>
                                    <span className="mt-1 text-[12px] font-semibold text-[#8A837C] dark:text-zinc-400">{t('typingPractice.minutes')}</span>
                                </button>
                            ))}
                        </div>
                        <p className="mt-3 font-hind text-[12.5px] text-[#8A837C] dark:text-zinc-400">{t('typingPractice.testNote')}</p>
                    </div>
                )}
                {mode === 'exam' && (
                    <div className="flex flex-col gap-4">
                        {/* Two steps as tabs: the post sets the target speed and time, the passage is what gets typed. */}
                        <div role="tablist" className="grid grid-cols-2 gap-1 rounded-2xl bg-[#F4F1ED] p-1 dark:bg-zinc-800">
                            {[
                                { id: 'post', label: t('typingPractice.examPost'), value: t(`typingPractice.exam.${EXAM_PRESETS[examPreset].id}`) },
                                { id: 'passage', label: t('typingPractice.passageLabel'), value: passages[examIndex]?.title },
                            ].map((tab, k) => {
                                const on = examTab === tab.id;
                                return (
                                    <button key={tab.id} type="button" role="tab" aria-selected={on} onClick={() => setExamTab(tab.id)}
                                        className={`flex min-w-0 items-center gap-2.5 rounded-xl px-3 py-2 text-left transition-all ${on
                                            ? 'bg-white shadow-[0_1px_3px_rgba(28,24,21,0.14)] dark:bg-zinc-700'
                                            : 'hover:bg-white/50 dark:hover:bg-zinc-700/40'}`}>
                                        <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-[12px] font-bold ${on ? 'bg-primary text-white' : 'bg-[#E6E0DA] text-[#8A837C] dark:bg-zinc-600 dark:text-zinc-300'}`}>{n(k + 1)}</span>
                                        <span className="min-w-0">
                                            <span className={`block font-hind text-[13.5px] font-bold leading-tight ${on ? 'text-primary' : 'text-[#5A534C] dark:text-zinc-300'}`}>{tab.label}</span>
                                            <span className="block truncate font-hind text-[11.5px] leading-tight text-[#8A837C] dark:text-zinc-400">{tab.value}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {examTab === 'post' ? (
                            <div role="tabpanel" className="grid gap-2.5 sm:grid-cols-3">
                                {EXAM_PRESETS.map((p, i) => {
                                    const active = examPreset === i;
                                    return (
                                        <button key={p.id} type="button" onClick={() => { setExamPreset(i); setExamTab('passage'); }} aria-pressed={active}
                                            className={`relative flex flex-col gap-3 rounded-2xl border p-4 pr-10 text-left transition-all ${active
                                                ? 'border-primary bg-primary/[0.06] shadow-[0_0_0_3px_rgba(230,138,0,0.10)]'
                                                : 'border-[#EEE9E4] hover:border-primary/40 dark:border-zinc-800'}`}>
                                            <CheckDot on={active} />
                                            <span className={`font-hind text-[14px] font-bold leading-snug ${active ? 'text-primary' : 'text-[#2C2722] dark:text-zinc-200'}`}>{t(`typingPractice.exam.${p.id}`)}</span>
                                            <span className="mt-auto flex items-baseline gap-2">
                                                <span className="text-[22px] font-bold leading-none tabular-nums text-primary">{n(isBanglaLayout ? p.bnTarget : p.enTarget)}</span>
                                                <span className="text-[11.5px] font-bold text-[#A39B93]">{t('typingPractice.wpmShort')}</span>
                                                <span className="ml-auto rounded-full bg-[#F4F1ED] px-2 py-0.5 text-[11px] font-bold text-[#7A726B] dark:bg-zinc-800 dark:text-zinc-400">{n(p.minutes)} {t('typingPractice.minutes')}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div role="tabpanel" className="grid gap-2.5 sm:grid-cols-2">
                                {passages.map((p, i) => {
                                    const active = examIndex === i;
                                    const words = p.text.split(/\s+/).filter(Boolean).length;
                                    return (
                                        <button key={p.id} type="button" onClick={() => setExamIndex(i)} aria-pressed={active}
                                            className={`relative flex flex-col gap-1.5 rounded-2xl border px-4 py-3.5 pr-10 text-left transition-all ${active
                                                ? 'border-primary bg-primary/[0.06] shadow-[0_0_0_3px_rgba(230,138,0,0.10)]'
                                                : 'border-[#EEE9E4] hover:border-primary/40 dark:border-zinc-800'}`}>
                                            <CheckDot on={active} />
                                            <span className="flex items-center gap-2">
                                                <span className={`font-hind text-[15px] font-bold ${active ? 'text-primary' : 'text-[#2C2722] dark:text-zinc-200'}`}>{p.title}</span>
                                                <span className="flex-none rounded-full bg-[#F4F1ED] px-2 py-0.5 text-[10.5px] font-bold text-[#7A726B] dark:bg-zinc-800 dark:text-zinc-400">{t('typingPractice.wordCount', { n: n(words) })}</span>
                                            </span>
                                            <span className="line-clamp-2 font-hind text-[12.5px] leading-snug text-[#8A837C] dark:text-zinc-400">{p.text}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* start bar */}
            <div className="flex flex-col gap-3 border-t border-[#F1EDE9] bg-[#FCFBFA] px-4 py-3.5 dark:border-zinc-800 dark:bg-zinc-900/60 sm:flex-row sm:items-center sm:px-5">
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#B0A89F] dark:text-zinc-500">{t(`typingPractice.mode.${mode}`)} · {t(`typingPractice.layout.${layoutId}`)}</div>
                    <div className="truncate font-hind text-[14px] font-semibold text-[#2C2722] dark:text-zinc-200">{summaryFor(mode)}</div>
                </div>
                <button type="button" onClick={() => start()}
                    className="group flex flex-none items-center justify-center gap-2.5 rounded-2xl bg-primary px-7 py-3.5 text-[15px] font-bold text-white shadow-[0_8px_20px_-6px_rgba(230,138,0,0.55)] transition-all hover:-translate-y-px hover:bg-primary/95 hover:shadow-[0_12px_24px_-8px_rgba(230,138,0,0.6)] active:translate-y-0">
                    <IconPlayerPlayFilled size={18} className="transition-transform group-hover:scale-110" />
                    {t('typingPractice.startBtn')}
                    <kbd className="ml-1 hidden rounded-md border border-white/40 px-1.5 py-px font-sans text-[10.5px] font-bold text-white/90 sm:inline-block">Enter</kbd>
                </button>
            </div>
        </section>
    );

    const rail = (
        <div className="flex flex-col gap-3.5">
            <section className="rounded-[24px] border border-[#EEE9E4] bg-white p-4 shadow-[0_1px_3px_rgba(28,24,21,0.05)] dark:border-zinc-800 dark:bg-zinc-900">
                <div className="mb-2 flex items-center gap-2">
                    <IconAdjustmentsHorizontal size={18} className="text-primary" />
                    <h3 className="font-hind text-[15px] font-bold text-[#1C1815] dark:text-zinc-100">{t('typingPractice.settings')}</h3>
                </div>
                <SettingsPanel prefs={prefs} onChange={changePrefs} t={t} n={n} />
            </section>

            <section className="rounded-[24px] border border-[#EEE9E4] bg-white p-4 shadow-[0_1px_3px_rgba(28,24,21,0.05)] dark:border-zinc-800 dark:bg-zinc-900">
                <div className="mb-2 flex items-center gap-2">
                    <IconHistory size={18} className="text-primary" />
                    <h3 className="font-hind text-[15px] font-bold text-[#1C1815] dark:text-zinc-100">{t('typingPractice.recentRuns')}</h3>
                    {history.length > 0 && <span className="rounded-full bg-[#F4F1ED] px-1.5 text-[10.5px] font-bold leading-[18px] text-[#8A837C] dark:bg-zinc-800 dark:text-zinc-400">{n(history.length)}</span>}
                    <button
                        type="button"
                        onClick={() => setResetOpen(true)}
                        disabled={!hasProgress}
                        title={t('typingPractice.reset.button')}
                        className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold text-[#8A837C] transition-colors hover:bg-red-50 hover:text-red-500 disabled:pointer-events-none disabled:opacity-40 dark:text-zinc-400 dark:hover:bg-red-900/20"
                    >
                        <IconRotate size={14} /> {t('typingPractice.reset.button')}
                    </button>
                </div>
                {history.length === 0 ? (
                    <p className="rounded-xl bg-[#FAF8F6] px-3 py-4 text-center font-hind text-[12.5px] text-[#A39B93] dark:bg-zinc-800/50 dark:text-zinc-500">{t('typingPractice.noRuns')}</p>
                ) : (
                    // Three rows show; the rest scroll inside the card.
                    <ul className="-mr-2 flex max-h-[168px] flex-col overflow-y-auto overscroll-contain pr-2">
                        {history.slice().reverse().map((run) => {
                            const Icon = MODE_ICON[run.mode] || IconBolt;
                            return (
                                <li key={run.at} className="flex h-14 flex-none items-center gap-3 border-b border-[#F4F1ED] last:border-0 dark:border-zinc-800">
                                    <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${MODE_TINT[run.mode] || MODE_TINT.practice}`}><Icon size={16} /></span>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-hind text-[13px] font-semibold text-[#2C2722] dark:text-zinc-200">{t(`typingPractice.mode.${run.mode}`)}</div>
                                        <div className="text-[11px] text-[#A39B93]">{timeAgo(run.at, t, n)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[15px] font-bold tabular-nums text-[#1C1815] dark:text-zinc-100">{n(Math.round(run.wpm))} <span className="text-[10px] font-semibold text-[#A39B93]">{t('typingPractice.wpmShort')}</span></div>
                                        <div className="text-[11px] font-semibold tabular-nums text-[#8A837C]">{n(Math.round(run.accuracy))}%</div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-[#B0A89F] dark:text-zinc-500">
                    <IconDeviceFloppy size={13} className="mt-px flex-none" /> {t('typingPractice.saveNote')}
                </p>
            </section>
        </div>
    );

    const controls = (
        <div ref={dashboardRef} className="flex flex-col gap-4">
            {hero}
            {modeCards}
            {config}
        </div>
    );

    const runIcon = MODE_ICON[runMode];

    const resetAll = () => {
        persist((prev) => resetProgress(prev));
        setLessonIndex(0);
        setExamIndex(0);
        setResetOpen(false);
    };

    return (
        <>
            <ToolPageLayout
                slug="general/typing-practice"
                subtitleKey="typingPractice.subtitle"
                status={phase === 'done' ? 'success' : phase === 'typing' ? 'processing' : 'ready'}
                statusText={statusText}
                howToSteps={howToSteps}
                variant="wideControls"
                mobilePreviewFirst={false}
                railWidthClass="lg:w-[360px]"
                controls={controls}
                preview={rail}
            />
            <ConfirmDialog
                open={resetOpen}
                onClose={() => setResetOpen(false)}
                onConfirm={resetAll}
                title={t('typingPractice.reset.title')}
                description={t('typingPractice.reset.description', { runs: n(totalRuns), lessons: n(totalLessons) })}
                confirmLabel={t('typingPractice.reset.confirm')}
            />
            {windowOpen && (
                <PracticeWindow
                    t={t} n={n} isBn={isBn}
                    title={`${t(`typingPractice.mode.${runMode}`)} · ${t(`typingPractice.layout.${layoutId}`)}`}
                    subtitle={summaryFor(runMode)}
                    icon={runIcon}
                    phase={phase}
                    session={session}
                    sessionKind={sessionKind}
                    mode={runMode}
                    layout={layout}
                    prefs={prefs}
                    onPrefs={changePrefs}
                    nextStroke={nextStroke}
                    pressedCode={pressedCode}
                    activeCodes={activeCodes}
                    shake={Date.now() - shakeAt < 200}
                    imeWarning={imeWarning}
                    wordSelected={wordSelected}
                    onSelectWord={() => setWordSelected(true)}
                    liveStats={liveStats}
                    clock={clock}
                    clockLabel={sessionKind === 'free' ? t('typingPractice.win.left') : t('typingPractice.time')}
                    clockUrgent={sessionKind === 'free' && typingStarted && remaining != null && remaining <= 10}
                    started={typingStarted}
                    settingsOpen={settingsOpen}
                    onToggleSettings={setSettingsOpen}
                    onRestart={() => start()}
                    onClose={closeWindow}
                    result={result}
                    resultProps={result ? {
                        kind: result.kind, data: result.data, stars: result.stars, passed: result.passed, chart: result.chart,
                        weakKeys: result.weakKeys, target: result.target ?? null,
                        n, t, isBn,
                        onRetry: () => start(),
                        onNext: runMode === 'learn' && result.passed && lessonIndex < course.length - 1 ? () => start({ lessonIndex: lessonIndex + 1 }) : null,
                        nextLabel: t('typingPractice.result.nextLesson'),
                        onClose: closeWindow,
                    } : null}
                />
            )}
        </>
    );
}

/* ---------------------------------------------------------------- helpers */

/** The current free-mode word has been typed in full and matches. */
function isWordComplete(session) {
    const i = session.index;
    return (session.rendered[i] || '').normalize('NFC') === (session.words[i] || '').normalize('NFC');
}

/** Per-key statistic key for the adaptive model: the physical key's character. */
function modelKeyOf(layoutId) {
    return (stroke) => {
        if (!stroke || stroke.code === 'Space') return null;
        const pair = QWERTY[stroke.code];
        if (!pair) return null;
        // English and Avro count letters (a and A together); Bijoy's shifted keys are different letters.
        if (layoutId !== 'bijoy') return /^[a-z]$/.test(pair[0]) ? pair[0] : pair[stroke.shift ? 1 : 0];
        return pair[stroke.shift ? 1 : 0];
    };
}

function updateKeyModelInProgress(progress, layoutId, samples) {
    const current = progress.layouts[layoutId].model || createKeyModel(layoutId);
    return saveModel(progress, layoutId, updateKeyModel(current, samples));
}

const firstUndone = (course, lessons) => {
    const i = course.findIndex((l) => !lessons[l.id]);
    return i < 0 ? course.length - 1 : i;
};

function timeAgo(at, t, n) {
    const mins = Math.floor((Date.now() - at) / 60000);
    if (mins < 1) return t('typingPractice.ago.now');
    if (mins < 60) return t('typingPractice.ago.min', { n: n(mins) });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('typingPractice.ago.hour', { n: n(hours) });
    return t('typingPractice.ago.day', { n: n(Math.floor(hours / 24)) });
}

/** The selection mark used on every choice card: an empty ring, filled orange with a tick when chosen. */
const CheckDot = ({ on }) => (
    <span className={`absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${on ? 'border-primary bg-primary text-white' : 'border-[#DDD6CF] dark:border-zinc-700'}`}>
        {on && <IconCheck size={12} stroke={3} />}
    </span>
);

const SectionHead = ({ children }) => (
    <h3 className="mb-2.5 px-1 font-hind text-[14px] font-bold text-[#3D3833] dark:text-zinc-300">{children}</h3>
);

const FieldLabel = ({ children }) => (
    <div className="mb-2.5 font-hind text-[13px] font-bold text-[#3D3833] dark:text-zinc-300">{children}</div>
);

function HeroStat({ icon: Icon, tint, label, value, unit }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-[#F1EDE9] bg-[#FCFBFA] px-3 py-3 dark:border-zinc-800 dark:bg-zinc-800/40">
            <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl ${tint}`}><Icon size={18} /></span>
            <div className="min-w-0">
                <div className="flex items-baseline gap-1">
                    <span className="text-[20px] font-bold leading-none tabular-nums text-[#1C1815] dark:text-zinc-100">{value}</span>
                    {unit ? <span className="text-[11px] font-semibold text-[#A39B93]">{unit}</span> : null}
                </div>
                <div className="mt-1 truncate font-hind text-[11.5px] text-[#8A837C] dark:text-zinc-400">{label}</div>
            </div>
        </div>
    );
}

function Sparkline({ values }) {
    const W = 300;
    const H = 32;
    const max = Math.max(10, ...values);
    const step = W / Math.max(1, values.length - 1);
    const pts = values.map((v, i) => [i * step, H - 3 - (v / max) * (H - 6)]);
    const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
    const [lx, ly] = pts[pts.length - 1];
    return (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-8 min-w-0 flex-1" aria-hidden="true">
            <path d={`${line} L${W} ${H} L0 ${H} Z`} fill="rgba(230,138,0,0.10)" />
            <path d={line} fill="none" stroke="#e68a00" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            <circle cx={lx} cy={ly} r="3" fill="#e68a00" />
        </svg>
    );
}

/** Lessons grouped by the row or skill they train, each a tile with its stars. */
function LessonMap({ course, lessons, value, onChange, n, t, keyLabel }) {
    const nextUp = course.findIndex((l) => !lessons[l.id]);
    const groups = [];
    course.forEach((lesson, i) => {
        const last = groups[groups.length - 1];
        if (last && last.focus === lesson.focus) last.items.push({ lesson, i });
        else groups.push({ focus: lesson.focus, items: [{ lesson, i }] });
    });
    const selected = course[value];
    const prevKeys = value > 0 ? course[value - 1].keys : '';
    const newKeys = selected.keys === 'ALL' ? [] : [...new Set(selected.keys.split('').filter((k) => !prevKeys.includes(k)))];
    const best = lessons[selected.id];

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <FieldLabel>{t('typingPractice.lessonLabel')}</FieldLabel>
                <span className="mb-2.5 rounded-full bg-[#F4F1ED] px-2.5 py-0.5 text-[11.5px] font-bold text-[#7A726B] dark:bg-zinc-800 dark:text-zinc-400">
                    {t('typingPractice.lessonNum', { n: n(value + 1), total: n(course.length) })}
                </span>
            </div>
            <div className="-mt-2 flex max-h-[360px] flex-col gap-3.5 overflow-y-auto pr-1">
                {groups.map((g, gi) => (
                    <div key={`${g.focus}${gi}`}>
                        <div className="mb-2 flex items-center gap-2.5">
                            <span className="font-hind text-[14px] font-bold text-[#6B635C] dark:text-zinc-400">{t(`typingPractice.focus.${g.focus}`)}</span>
                            <span className="h-px flex-1 bg-[#F1EDE9] dark:bg-zinc-800" />
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6">
                            {g.items.map(({ lesson, i }) => {
                                const done = lessons[lesson.id];
                                const active = value === i;
                                return (
                                    <button key={lesson.id} type="button" onClick={() => onChange(i)}
                                        className={`relative flex min-h-[96px] flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-2.5 transition-all ${active
                                            ? 'border-primary bg-primary/[0.06] shadow-[0_0_0_3px_rgba(230,138,0,0.12)]'
                                            : done
                                                ? 'border-emerald-200/80 bg-emerald-50/40 hover:border-primary/40 dark:border-emerald-900/40 dark:bg-emerald-900/10'
                                                : 'border-[#EEE9E4] hover:border-primary/40 dark:border-zinc-800'}`}>
                                        <span className={`absolute left-2.5 top-2 text-[12px] font-bold ${active ? 'text-primary' : 'text-[#B0A89F]'}`}>{n(i + 1)}</span>
                                        {/* Selected: filled orange check, like the mode cards. Done: a small green tick. */}
                                        {active ? (
                                            <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white shadow-[0_2px_6px_rgba(230,138,0,0.4)]">
                                                <IconCheck size={12} stroke={3} />
                                            </span>
                                        ) : done ? <IconCheck size={14} stroke={3} className="absolute right-2.5 top-2.5 text-emerald-500" /> : null}
                                        {i === nextUp && !active && (
                                            <span className="absolute right-1.5 top-1.5 rounded-full bg-primary px-1.5 text-[9.5px] font-bold leading-[16px] text-white">{t('typingPractice.nextUp')}</span>
                                        )}
                                        <span className={`mt-3 line-clamp-2 text-center font-hind text-[19px] font-semibold leading-snug ${active ? 'text-primary' : 'text-[#1C1815] dark:text-zinc-100'}`}>{lesson.title}</span>
                                        {done ? <Stars value={done.stars} size={11} /> : <span className="h-[11px]" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-[#FAF8F6] px-4 py-3 dark:bg-zinc-800/50">
                {newKeys.length > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="font-hind text-[12px] font-semibold text-[#8A837C] dark:text-zinc-400">{t('typingPractice.newKeys')}</span>
                        <div className="flex flex-wrap gap-1">
                            {newKeys.map((k) => <KeyCap key={k}>{keyLabel(k)}</KeyCap>)}
                        </div>
                    </div>
                )}
                {best ? (
                    <div className="ml-auto flex items-center gap-2 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                        <span>{t('typingPractice.bestScore')}</span>
                        <Stars value={best.stars} size={13} />
                        <span className="tabular-nums">{n(Math.round(best.wpm))} {t('typingPractice.wpmShort')}</span>
                    </div>
                ) : (
                    <span className="ml-auto font-hind text-[12px] text-[#A39B93]">{t('typingPractice.notTried')}</span>
                )}
            </div>
        </div>
    );
}

const KeyCap = ({ children, tone }) => (
    <span className={`inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border px-1.5 font-hind text-[15px] font-semibold shadow-[0_2px_0_#E5DFD8] dark:shadow-[0_2px_0_#18181b] ${tone || 'border-[#E5DFD8] bg-white text-[#2C2722] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100'}`}>
        {children}
    </span>
);

function AdaptivePanel({ adaptive, model, progress, keyLabel, n, t }) {
    const order = new Set(model.order);
    const weak = Object.entries(model.times || {})
        .filter(([k]) => order.has(k))
        .map(([k, time]) => ({ k, conf: msPerStrokeAt(ADAPTIVE_TARGET) / time }))
        .filter((x) => x.conf < 1).sort((a, b) => a.conf - b.conf).slice(0, 6);
    const pct = Math.round(progress * 100);
    const r = 30;
    const c = 2 * Math.PI * r;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
                <div className="relative flex h-[78px] w-[78px] flex-none items-center justify-center">
                    <svg viewBox="0 0 72 72" className="absolute inset-0 -rotate-90">
                        <circle cx="36" cy="36" r={r} fill="none" strokeWidth="7" className="stroke-[#EFEBE7] dark:stroke-zinc-800" />
                        <circle cx="36" cy="36" r={r} fill="none" stroke="#e68a00" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
                    </svg>
                    <span className="text-[17px] font-bold tabular-nums text-[#1C1815] dark:text-zinc-100">{n(pct)}%</span>
                </div>
                <div>
                    <div className="font-hind text-[15px] font-bold text-[#1C1815] dark:text-zinc-100">{t('typingPractice.adaptive.title')}</div>
                    <p className="mt-0.5 font-hind text-[12.5px] leading-snug text-[#8A837C] dark:text-zinc-400">
                        {t('typingPractice.adaptive.unlocked', { n: n(adaptive.included.length) })} · {t('typingPractice.adaptive.target', { n: n(ADAPTIVE_TARGET) })}
                    </p>
                </div>
            </div>

            <div>
                <FieldLabel>{t('typingPractice.adaptive.keys')}</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                    {model.order.map((k) => {
                        const included = adaptive.included.includes(k);
                        const focus = adaptive.focus === k;
                        return (
                            <KeyCap key={k} tone={focus
                                ? 'border-primary bg-primary text-white shadow-[0_2px_0_#b36b00]'
                                : included ? undefined : 'border-dashed border-[#E5DFD8] bg-transparent text-[#CFC8C0] shadow-none dark:border-zinc-700 dark:text-zinc-600'}>
                                {keyLabel(k)}
                            </KeyCap>
                        );
                    })}
                </div>
                <p className="mt-2 font-hind text-[11.5px] text-[#A39B93]">{t('typingPractice.adaptive.legend')}</p>
            </div>

            {weak.length > 0 && (
                <div>
                    <FieldLabel>{t('typingPractice.adaptive.weak')}</FieldLabel>
                    <div className="flex flex-wrap gap-1.5">
                        {weak.map((w) => (
                            <KeyCap key={w.k} tone="border-red-200 bg-red-50 text-red-600 shadow-none dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-400">{keyLabel(w.k)}</KeyCap>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
